import { useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Download, Building2, RefreshCw, Upload } from "lucide-react";

import { ProspectSearchView } from "@/components/prospects/ProspectSearchView";
import { ProspectPipelineView } from "@/components/prospects/ProspectPipelineView";

export default function ProspectSearchPage({ initialView = "search" }) {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);

  // Parse a CSV file and bulk-import + deep-analyze
  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) { toast.error("CSV is empty"); setImporting(false); return; }
      const split = (l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      const header = split(lines[0]).map((h) => h.toLowerCase());
      const idx = (names) => header.findIndex((h) => names.some((n) => h.includes(n)));
      let nameI = idx(["name", "company", "business"]);
      let domI = idx(["domain", "website", "url", "site"]);
      let cityI = idx(["city"]), stateI = idx(["state"]), catI = idx(["category", "industry", "type"]), phoneI = idx(["phone"]);
      const hasHeader = nameI >= 0 || domI >= 0;
      const rows = hasHeader ? lines.slice(1) : lines;
      if (!hasHeader) { nameI = 0; domI = 1; }
      const clean = (u) => (u || "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "").trim() || null;
      const prospects = rows.map((l) => {
        const c = split(l);
        return {
          name: nameI >= 0 ? c[nameI] : "",
          domain: domI >= 0 ? clean(c[domI]) : null,
          city: cityI >= 0 ? c[cityI] : "",
          state: stateI >= 0 ? c[stateI] : "",
          category: catI >= 0 ? c[catI] : "",
          phone: phoneI >= 0 ? c[phoneI] : null,
        };
      }).filter((p) => p.name || p.domain);
      if (!prospects.length) { toast.error("No valid rows found (need a name or domain column)"); setImporting(false); return; }
      const res = await fetch(`${API}/companies/bulk-import-analyze`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prospects }),
      });
      const data = await res.json();
      toast.success(`Imported ${data.created} prospects — deep analysis running in the background`);
      setView("saved"); fetchSaved();
    } catch (err) {
      toast.error("Import failed — check the CSV format");
    } finally { setImporting(false); }
  };

  // Search state
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedRevenue, setSelectedRevenue] = useState(new Set());
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [minRating, setMinRating] = useState("0");
  const [showFilters, setShowFilters] = useState(false);

  // Results
  const [results, setResults] = useState([]);
  const [savedCompanies, setSavedCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [view, setView] = useState(initialView);
  const [savingIds, setSavingIds] = useState(new Set());
  const [selectedSearch, setSelectedSearch] = useState(new Set());
  const [selectedSaved, setSelectedSaved] = useState(new Set());
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const isAdmin = user?.role === "admin";
  const [team, setTeam] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("all");

  const [savedTotal, setSavedTotal] = useState(0);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const fetchSeq = useRef(0);

  const fetchSaved = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setPipelineLoading(true);
    try {
      const qs = new URLSearchParams({ limit: "500" });
      if (ownerFilter !== "all") qs.set("owner", ownerFilter);
      const res = await fetch(`${API}/companies?${qs}`, { credentials: "include" });
      if (res.ok && seq === fetchSeq.current) {
        const data = await res.json();
        setSavedCompanies(data.companies || []);
        setSavedTotal(data.total ?? (data.companies || []).length);
        setSelectedSaved(new Set());
      }
    } catch (e) { console.error(e); }
    finally { if (seq === fetchSeq.current) setPipelineLoading(false); }
  }, [API, ownerFilter]);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);
  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API}/users`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => setTeam(d.users || []))
      .catch(() => {});
  }, [API, isAdmin]);

  const handleSearch = useCallback(async () => {
    setLoading(true); setSearched(true); setSelectedSearch(new Set());
    try {
      const params = new URLSearchParams();
      if (keyword) params.set("keyword", keyword);
      if (city) params.set("city", city);
      if (state) params.set("state", state);
      if (selectedCategory) params.set("category", selectedCategory);
      if (parseFloat(minRating) > 0) params.set("min_rating", minRating);
      if (selectedRevenue.size > 0) params.set("revenue_ranges", [...selectedRevenue].join(","));
      if (selectedEmployees.size > 0) params.set("employee_ranges", [...selectedEmployees].join(","));

      const res = await fetch(`${API}/companies/search?${params}`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setResults(data.results || []); }
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  }, [API, keyword, city, state, selectedCategory, minRating, selectedRevenue, selectedEmployees]);

  const toggleFilter = (set, setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSelectedCategory("");
    setSelectedRevenue(new Set());
    setSelectedEmployees(new Set());
    setMinRating("0");
  };

  const handleSaveSingle = async (prospect, idx) => {
    setSavingIds((p) => new Set(p).add(idx));
    try {
      const res = await fetch(`${API}/companies`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(prospect),
      });
      if (res.ok) {
        toast.success(`${prospect.name} saved + audit started`);
        fetchSaved();
      }
    } catch { toast.error("Failed to save"); }
    finally { setSavingIds((p) => { const s = new Set(p); s.delete(idx); return s; }); }
  };

  const handleBulkSave = async () => {
    if (selectedSearch.size === 0) return;
    setBulkSaving(true);
    const prospects = [...selectedSearch].map((i) => results[i]);
    try {
      const res = await fetch(`${API}/companies/bulk`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ prospects }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.count} prospects saved + audits started`);
        setSelectedSearch(new Set());
        fetchSaved();
      }
    } catch { toast.error("Bulk save failed"); }
    finally { setBulkSaving(false); }
  };

  const handleBulkDelete = async () => {
    if (selectedSaved.size === 0) return;
    const ids = [...selectedSaved].map((i) => savedCompanies[i]?.company_id).filter(Boolean);
    try {
      const res = await fetch(`${API}/companies/bulk`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ company_ids: ids }),
      });
      if (res.ok) {
        toast.success(`${(await res.json()).deleted} removed`);
        setSelectedSaved(new Set());
        fetchSaved();
      }
    } catch { toast.error("Delete failed"); }
  };

  const handleBulkSync = async () => {
    if (selectedSaved.size === 0) return;
    const ids = [...selectedSaved].map((i) => savedCompanies[i]?.company_id).filter(Boolean);
    setBulkSyncing(true);
    const t = toast.loading(`Syncing ${ids.length} prospect${ids.length > 1 ? "s" : ""} to Monday…`);
    try {
      const res = await fetch(`${API}/crm/sync-bulk`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ company_ids: ids, platform: "monday" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Bulk sync failed", { id: t }); return; }
      if (data.failed > 0) {
        const firstErr = (data.results || []).find((r) => r.error)?.error;
        toast.warning(`${data.synced} synced, ${data.failed} skipped${firstErr ? ` — ${firstErr}` : ""}`, { id: t, duration: 6000 });
      } else {
        toast.success(`${data.synced} prospect${data.synced > 1 ? "s" : ""} synced to Monday`, { id: t });
      }
      setSelectedSaved(new Set());
      fetchSaved();
    } catch { toast.error("Bulk sync failed", { id: t }); }
    finally { setBulkSyncing(false); }
  };

  const handleExport = async () => {
    try {
      const ids = selectedSaved.size > 0
        ? [...selectedSaved].map((i) => savedCompanies[i]?.company_id).filter(Boolean) : null;
      const res = await fetch(`${API}/export/prospects`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ format: "csv", company_ids: ids }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "prospects.csv"; a.click();
        URL.revokeObjectURL(url); toast.success("Exported");
      } else { toast.error((await res.json()).detail || "Export failed"); }
    } catch { toast.error("Export failed"); }
  };

  const toggleSearchSelect = (i) => setSelectedSearch((p) => { const s = new Set(p); s.has(i) ? s.delete(i) : s.add(i); return s; });
  const toggleSavedSelect = (i) => setSelectedSaved((p) => { const s = new Set(p); s.has(i) ? s.delete(i) : s.add(i); return s; });
  const toggleAllSearch = () => selectedSearch.size === results.length ? setSelectedSearch(new Set()) : setSelectedSearch(new Set(results.map((_, i) => i)));
  const toggleAllSaved = () => selectedSaved.size === savedCompanies.length ? setSelectedSaved(new Set()) : setSelectedSaved(new Set(savedCompanies.map((_, i) => i)));

  const isUnread = (c) => c.read === false && c.source === "embed_form" && c.created_by === user?.user_id;
  const unreadCount = savedCompanies.filter(isUnread).length;

  const activeFilterCount = [selectedRevenue.size > 0, selectedEmployees.size > 0, parseFloat(minRating) > 0, !!selectedCategory].filter(Boolean).length;

  return (
    <div data-testid="prospect-search-page" className="space-y-4 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Prospects</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Search, audit, and manage business prospects</p>
        </div>
        <div className="flex gap-1.5">
          <Button data-testid="toggle-search" size="sm"
            variant={view === "search" ? "default" : "outline"}
            onClick={() => setView("search")}
            className={`h-8 text-xs ${view === "search" ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}>
            <Search className="w-3.5 h-3.5 mr-1" /> Search
          </Button>
          <Button data-testid="toggle-saved" size="sm"
            variant={view === "saved" ? "default" : "outline"}
            onClick={() => { setView("saved"); fetchSaved(); }}
            className={`h-8 text-xs relative ${view === "saved" ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}>
            <Building2 className="w-3.5 h-3.5 mr-1" /> Pipeline ({savedTotal})
            {unreadCount > 0 && (
              <span data-testid="pipeline-unread-badge" className="ml-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold inline-flex items-center justify-center">
                {unreadCount} new
              </span>
            )}
          </Button>
          <Button data-testid="export-btn" variant="outline" size="sm" onClick={handleExport} className="h-8 text-xs">
            <Download className="w-3.5 h-3.5 mr-1" /> Export
          </Button>
          <input type="file" accept=".csv,text/csv" id="csv-import-input" data-testid="csv-import-input" className="hidden" onChange={handleImportCsv} />
          <Button data-testid="import-csv-btn" variant="outline" size="sm" disabled={importing}
            onClick={() => document.getElementById("csv-import-input").click()}
            className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            title="Upload a CSV of companies/domains to bulk deep-analyze">
            {importing ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Import &amp; Analyze
          </Button>
        </div>
      </div>

      {view === "search" && (
        <ProspectSearchView
          keyword={keyword} setKeyword={setKeyword} city={city} setCity={setCity} state={state} setState={setState}
          handleSearch={handleSearch} loading={loading} showFilters={showFilters} setShowFilters={setShowFilters}
          activeFilterCount={activeFilterCount} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
          selectedRevenue={selectedRevenue} setSelectedRevenue={setSelectedRevenue}
          selectedEmployees={selectedEmployees} setSelectedEmployees={setSelectedEmployees}
          minRating={minRating} setMinRating={setMinRating} toggleFilter={toggleFilter} clearAllFilters={clearAllFilters}
          results={results} searched={searched} selectedSearch={selectedSearch}
          toggleSearchSelect={toggleSearchSelect} toggleAllSearch={toggleAllSearch}
          savingIds={savingIds} handleSaveSingle={handleSaveSingle} handleBulkSave={handleBulkSave} bulkSaving={bulkSaving} />
      )}

      {view === "saved" && (
        <ProspectPipelineView
          savedCompanies={savedCompanies} selectedSaved={selectedSaved}
          toggleSavedSelect={toggleSavedSelect} toggleAllSaved={toggleAllSaved}
          handleBulkSync={handleBulkSync} bulkSyncing={bulkSyncing} handleExport={handleExport}
          handleBulkDelete={handleBulkDelete} isUnread={isUnread} navigate={navigate}
          isAdmin={isAdmin} team={team} ownerFilter={ownerFilter} setOwnerFilter={setOwnerFilter}
          savedTotal={savedTotal} pipelineLoading={pipelineLoading} />
      )}
    </div>
  );
}
